// PayloadCard.tsx — the RICH decrypted-payload card in the Ledger's PROTECTED
// CONTENT · TDF addendum (replaces the raw plaintext dump).
// CONTRACT:C4-DESIGN-TOKENS.1.0 (the visual family) — parsing/adapters live in
// payloadParse.ts (CONTRACT:C2-RENDER-ENGINE.1.0); marking strings/colors come
// from dod-marking.ts (CONTRACT:I2-SECURITY-LABELS.1.0).
//
// Renders a KAS-RELEASED plaintext (post-grant only — this component never
// decides WHAT decrypts; presentation-only, I13):
//   HEADER — the payload's full CAPCO marking line (bannerLine) on its banner
//     tier colors, preferring the payload's own parsed `marking` over the host
//     prop. This is a CARD HEADER, not a screen banner — the banner-purity rule
//     (Ledger.tsx: banners carry CLASSIFICATION ONLY) applies to the mini-banner
//     above the card, which stays untouched; the FULL line belongs here, where a
//     cleared reader inspects the released content. With no marking at all the
//     header degrades to the striped "[ PAYLOAD · RELEASED ]" strip.
//   BODY — structured payloads (frontgate `{marking, fields}`) render
//     `fields.detail` as prose plus every remaining field (name/theater/source +
//     ANY unknown keys) in the Ledger's labeled-grid look; plain-string payloads
//     (live gateway / seeded prose) render as whitespace-preserving prose.
//   VIEW RAW — a collapsed <details> (native, no React state — React stays OUT
//     of the render loop) mirroring RawCoTDisclosure: the EXACT plaintext bytes
//     stay one click away in a <pre>, with best-effort copy.
//
// FAIL-OPEN ON DISPLAY SHAPE ONLY: an unrecognized shape renders as text —
// never a throw, never a clearance decision.

import type { InnerMarking } from "../host/kas.ts";
import { parseSealedPayload, markingToHighWater, compactValue } from "./payloadParse.ts";
import { bannerLine, bannerTier, bannerColor, bannerTextColor } from "./dod-marking.ts";
import "./ledger.css";

/** "view raw" disclosure for the released plaintext — the exact bytes the KAS
 *  rewrap yielded, one click away (mirrors Ledger's RawCoTDisclosure). */
function RawPlaintextDisclosure({ raw }: { raw: string }): JSX.Element {
  return (
    <details className="ledger__payload-card-raw">
      <summary className="ledger__payload-card-raw-summary">
        VIEW RAW · DECRYPTED PLAINTEXT
      </summary>
      <div className="ledger__payload-card-raw-body">
        <button
          type="button"
          className="ledger__payload-card-raw-copy"
          onClick={() => {
            // Best-effort copy; silently degrade where the clipboard API is
            // absent (e.g. non-secure context). Never throws into the render path.
            try {
              void navigator?.clipboard?.writeText?.(raw);
            } catch {
              /* clipboard unavailable — the <pre> below is still selectable */
            }
          }}
        >
          Copy
        </button>
        <pre className="ledger__payload-card-raw-pre">{raw}</pre>
      </div>
    </details>
  );
}

export function PayloadCard({
  plaintext,
  marking,
}: {
  /** The KAS-released plaintext (status.decrypt.plaintext) — already granted. */
  plaintext: string;
  /** The host-parsed inner marking (status.decrypt.marking), the fallback when
   *  the payload itself carries none (plain-prose shapes B/C). */
  marking?: InnerMarking;
}): JSX.Element {
  const parsed = parseSealedPayload(plaintext);
  // Prefer the payload's OWN self-declared marking; else the host prop. Both are
  // post-grant display inputs — neither changes what was released.
  const m = (parsed.kind === "structured" ? parsed.marking : undefined) ?? marking;
  const hw = m ? markingToHighWater(m) : null;
  const tier = hw ? bannerTier(hw) : null;

  const detail =
    parsed.kind === "structured" && typeof parsed.fields.detail === "string" && parsed.fields.detail.trim() !== ""
      ? parsed.fields.detail
      : null;
  const gridEntries =
    parsed.kind === "structured"
      ? Object.entries(parsed.fields).filter(([k]) => k !== "detail")
      : [];

  return (
    <div className="ledger__payload-card">
      {hw && tier ? (
        <div
          className="ledger__payload-card-head"
          style={{ background: bannerColor(tier), color: bannerTextColor(tier) }}
        >
          {bannerLine(hw)}
        </div>
      ) : (
        <div className="ledger__payload-card-head ledger__payload-card-head--released">
          [ PAYLOAD · RELEASED ]
        </div>
      )}
      <div className="ledger__payload-card-body">
        {parsed.kind === "structured" ? (
          <>
            {detail && <p className="ledger__payload-card-prose">{detail}</p>}
            {gridEntries.length > 0 && (
              <div className="ledger__op-grid ledger__payload-card-grid">
                {gridEntries.map(([k, v]) => (
                  <div className="ledger__op-cell" key={k}>
                    <div className="ledger__op-k">{k.toUpperCase()}</div>
                    <div className="ledger__op-v">{compactValue(v)}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="ledger__payload-card-prose ledger__payload-card-prose--pre">{parsed.text}</p>
        )}
        <RawPlaintextDisclosure raw={plaintext} />
      </div>
    </div>
  );
}
